import { type CSSProperties, useId } from 'react';
import './EpcProjectIllustration.css';

export type EpcIllustrationVariant = 'solar' | 'wind' | 'hybrid' | 'bess' | 'generic';

export interface EpcProjectIllustrationProps {
  variant: EpcIllustrationVariant;
  /** Theme accent hex, e.g. '#ea580c'. Wired through CSS custom property `--epc-accent`. */
  accent: string;
  className?: string;
}

type SvgRootStyle = CSSProperties & { '--epc-accent': string };

const SVG_PROPS = {
  viewBox: '0 0 240 120',
  width: '100%',
  height: '100%',
  preserveAspectRatio: 'xMidYMid meet' as const,
  role: 'img' as const,
  'aria-hidden': true,
};

const MINI_SVG_PROPS = {
  viewBox: '0 0 32 32',
  width: '100%',
  height: '100%',
  preserveAspectRatio: 'xMidYMid meet' as const,
  role: 'img' as const,
  'aria-hidden': true,
};

export default function EpcProjectIllustration({ variant, accent, className }: EpcProjectIllustrationProps) {
  const style: SvgRootStyle = { '--epc-accent': accent };
  const rootClass = `epc-illust-root${className ? ` ${className}` : ''}`;

  switch (variant) {
    case 'solar':
      return <SolarIllustration className={rootClass} style={style} />;
    case 'wind':
      return <WindIllustration className={rootClass} style={style} />;
    case 'hybrid':
      return <HybridIllustration className={rootClass} style={style} />;
    case 'bess':
      return <BessIllustration className={rootClass} style={style} />;
    default:
      return <GenericIllustration className={rootClass} style={style} />;
  }
}

interface VariantProps {
  className: string;
  style: SvgRootStyle;
}

// ---------- SOLAR ----------
// Flat-corporate style per ~/.claude/skills/flat-corporate-illustration:
// technician character (yellow workwear + orange hard hat) on left,
// large tilted solar panel on right, sun + rays upper-right, plant cluster
// bottom-right. Subtle character bob, sun ray rotation, panel gleam sweep.
function SolarIllustration({ className, style }: VariantProps) {
  return (
    <svg {...SVG_PROPS} className={className} style={style}>
      <title>Solar project technician inspecting panel array</title>
      <defs>
        <radialGradient id="epc-solar-sun" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFF1C4" />
          <stop offset="55%" stopColor="var(--epc-accent)" />
          <stop offset="100%" stopColor="var(--epc-accent)" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="epc-solar-gleam" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="50%" stopColor="#ffffff" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <clipPath id="epc-solar-panel-clip">
          <polygon points="112,100 222,100 228,70 104,70" />
        </clipPath>
      </defs>

      {/* Soft ground shadow puddle */}
      <ellipse cx="160" cy="108" rx="78" ry="3.5" fill="#3D4452" opacity="0.07" />
      <ellipse cx="52" cy="109" rx="22" ry="2.6" fill="#3D4452" opacity="0.09" />

      {/* Sun upper-right */}
      <circle cx="200" cy="26" r="20" fill="url(#epc-solar-sun)" opacity="0.45" />
      <g transform="translate(200 26)">
        <g className="epc-illust-spin-sun">
          {Array.from({ length: 8 }).map((_, i) => {
            const angle = (i * 360) / 8;
            return (
              <line
                key={i}
                x1="0"
                y1="-14"
                x2="0"
                y2="-21"
                stroke="var(--epc-accent)"
                strokeWidth="1.5"
                strokeLinecap="round"
                opacity="0.85"
                transform={`rotate(${angle})`}
              />
            );
          })}
        </g>
      </g>
      <circle cx="200" cy="26" r="8" fill="var(--epc-accent)" className="epc-illust-pulse" />
      <circle cx="200" cy="26" r="3.5" fill="#FFF1C4" opacity="0.95" />

      {/* Solar panel — tilted, dark slate, with internal grid */}
      <g>
        <polygon points="112,100 222,100 228,70 104,70" fill="#3D4452" stroke="#2D3340" strokeWidth="0.7" />
        {/* vertical cell dividers (6 cols) */}
        {Array.from({ length: 5 }).map((_, i) => {
          const t = (i + 1) / 6;
          const xTop = 104 + (228 - 104) * t;
          const xBot = 112 + (222 - 112) * t;
          return <line key={`vA-${i}`} x1={xTop} y1="70" x2={xBot} y2="100" stroke="#5A6275" strokeWidth="0.6" />;
        })}
        {/* horizontal cell dividers (3 rows) */}
        {Array.from({ length: 2 }).map((_, i) => {
          const t = (i + 1) / 3;
          const yLine = 70 + 30 * t;
          const xL = 104 + (112 - 104) * t;
          const xR = 228 + (222 - 228) * t;
          return <line key={`hA-${i}`} x1={xL} y1={yLine} x2={xR} y2={yLine} stroke="#5A6275" strokeWidth="0.6" />;
        })}
        {/* gleam sweep */}
        <g clipPath="url(#epc-solar-panel-clip)">
          <rect
            className="epc-illust-gleam"
            x="80"
            y="68"
            width="55"
            height="34"
            fill="url(#epc-solar-gleam)"
            transform="skewX(-28)"
          />
        </g>
        {/* support legs */}
        <line x1="132" y1="100" x2="126" y2="112" stroke="#3D4452" strokeWidth="1.7" strokeLinecap="round" />
        <line x1="200" y1="100" x2="206" y2="112" stroke="#3D4452" strokeWidth="1.7" strokeLinecap="round" />
      </g>

      {/* Plant cluster bottom-right corner */}
      <g>
        <path d="M 225 110 Q 220 96 222 90 Q 228 98 226 110 Z" fill="#6FA08C" />
        <path d="M 230 110 Q 234 94 238 96 Q 234 104 232 110 Z" fill="#3F7259" />
        <path d="M 220 110 Q 217 102 219 98 Q 222 104 221 110 Z" fill="#3F7259" />
      </g>

      {/* Technician character (left). Wrapper bobs gently. */}
      <g className="epc-illust-bob">
        {/* shadow contact under feet (subtle) */}
        {/* boots */}
        <rect x="42" y="100" width="6" height="6" rx="1" fill="#2D2E3A" />
        <rect x="52" y="100" width="6" height="6" rx="1" fill="#2D2E3A" />
        {/* legs */}
        <rect x="42" y="80" width="6" height="22" rx="1.5" fill="#F4C24D" />
        <rect x="52" y="80" width="6" height="22" rx="1.5" fill="#F4C24D" />
        {/* torso — slight trapezoid (broader shoulders, narrower waist) */}
        <path d="M 38 60 L 62 60 L 64 84 L 36 84 Z" fill="#F4C24D" />
        {/* belt */}
        <rect x="36" y="80" width="28" height="2.6" fill="#8C5A3F" />
        {/* belt buckle */}
        <rect x="48" y="80" width="4" height="2.6" fill="#5A3A2C" />
        {/* left arm down at side */}
        <path d="M 36 62 L 30 80 L 33 84 L 41 70 Z" fill="#F4C24D" />
        {/* right arm forward holding clipboard */}
        <path d="M 64 62 L 74 68 L 72 73 L 62 70 Z" fill="#F4C24D" />
        {/* hands */}
        <circle cx="32" cy="80" r="2" fill="#F2C29C" />
        <circle cx="72" cy="69" r="2.2" fill="#F2C29C" />
        {/* clipboard in right hand */}
        <g transform="translate(70 56)">
          <rect x="0" y="0" width="14" height="18" rx="1.2" fill="#F0EDE4" stroke="#3D4452" strokeWidth="0.5" />
          <rect x="4" y="-2" width="6" height="3.2" rx="0.6" fill="#3D4452" />
          <line x1="2" y1="6" x2="12" y2="6" stroke="#94a3b8" strokeWidth="0.6" />
          <line x1="2" y1="9.5" x2="12" y2="9.5" stroke="#94a3b8" strokeWidth="0.6" />
          <line x1="2" y1="13" x2="10" y2="13" stroke="#94a3b8" strokeWidth="0.6" />
        </g>
        {/* neck */}
        <rect x="46" y="55" width="8" height="6" fill="#F2C29C" />
        {/* hair back */}
        <path d="M 41 50 Q 41 38 50 36 Q 59 38 59 50 L 59 54 L 41 54 Z" fill="#2D2E3A" />
        {/* head */}
        <circle cx="50" cy="48" r="8.5" fill="#F2C29C" />
        {/* eyes */}
        <circle cx="47" cy="49" r="0.9" fill="#2D2E3A" />
        <circle cx="53" cy="49" r="0.9" fill="#2D2E3A" />
        {/* smile */}
        <path d="M 48 53.2 Q 50 54.6 52 53.2" stroke="#2D2E3A" strokeWidth="0.7" fill="none" strokeLinecap="round" />
        {/* hard hat — orange dome + brim */}
        <path d="M 40 42 Q 50 32 60 42 L 60 43.5 L 40 43.5 Z" fill="#F08A3E" />
        <ellipse cx="50" cy="44" rx="11.5" ry="1.7" fill="#F08A3E" />
        {/* hat band */}
        <rect x="40" y="42.7" width="20" height="1.2" fill="#2D2E3A" opacity="0.32" />
        {/* hat top highlight */}
        <path d="M 44 36 Q 47 33 50 33" stroke="#FFFFFF" strokeWidth="0.9" strokeLinecap="round" fill="none" opacity="0.45" />
      </g>
    </svg>
  );
}

// ---------- WIND ----------
function WindIllustration({ className, style }: VariantProps) {
  return (
    <svg {...SVG_PROPS} className={className} style={style}>
      <title>Wind project illustration</title>
      <defs>
        <linearGradient id="epc-wind-blade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--epc-accent)" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#cbd5e1" stopOpacity="0.95" />
        </linearGradient>
      </defs>

      {/* Sky tint subtle clouds */}
      <ellipse cx="40" cy="32" rx="22" ry="4" fill="#e2e8f0" opacity="0.7" />
      <ellipse cx="210" cy="22" rx="18" ry="3" fill="#e2e8f0" opacity="0.55" />

      {/* Horizon */}
      <line x1="0" y1="96" x2="240" y2="96" stroke="#e2e8f0" strokeWidth="1" />

      {/* Background turbine 1 (slow, smaller, right-far) */}
      <BackgroundTurbine cx={200} cy={70} scale={0.55} spinClass="epc-illust-spin-slow" />

      {/* Background turbine 2 (slower, smaller still) */}
      <BackgroundTurbine cx={168} cy={78} scale={0.4} spinClass="epc-illust-spin-slower" />

      {/* Foreground turbine — center-left, tall */}
      <g>
        {/* Tower (tapered): wider at base, narrow at top */}
        <polygon points="84,96 76,96 78,38 82,38" fill="#94a3b8" />
        {/* Tower highlight */}
        <line x1="80" y1="38" x2="80" y2="96" stroke="#cbd5e1" strokeWidth="0.6" />
        {/* Nacelle */}
        <rect x="72" y="32" width="16" height="8" rx="2.5" fill="#64748b" />
        <circle cx="86" cy="36" r="2.2" fill="#475569" />
        {/* Foreground blades — drawn around (0,0), translated to hub */}
        <g transform="translate(86 36)">
          <g className="epc-illust-spin">
            {[0, 120, 240].map((angle) => (
              <path
                key={angle}
                d="M 0 0 C -1.5 -10 -2.5 -22 -1 -34 C 0 -36 1 -36 1.4 -34 C 2.5 -22 1.5 -10 0 0 Z"
                fill="url(#epc-wind-blade)"
                stroke="var(--epc-accent)"
                strokeOpacity="0.35"
                strokeWidth="0.5"
                transform={`rotate(${angle})`}
              />
            ))}
            {/* phantom bounding circle — forces fill-box to be symmetric around (0,0)
                so transform-origin:50% 50% lands exactly on the hub */}
            <circle cx="0" cy="0" r="37" fill="none" />
            {/* hub cap */}
            <circle cx="0" cy="0" r="2" fill="#475569" />
          </g>
        </g>
      </g>

      {/* Ground shadow */}
      <ellipse cx="80" cy="100" rx="14" ry="2" fill="#0f172a" opacity="0.07" />
    </svg>
  );
}

interface BackgroundTurbineProps {
  cx: number;
  cy: number;
  scale: number;
  spinClass: string;
}

function BackgroundTurbine({ cx, cy, scale, spinClass }: BackgroundTurbineProps) {
  // Hub at (cx, cy). Tower extends from hub down to horizon (96).
  const towerBottom = 96;
  const towerHeight = towerBottom - cy;
  return (
    <g opacity="0.45">
      <polygon
        points={`${cx - 1.2 * scale},${towerBottom} ${cx + 1.2 * scale},${towerBottom} ${cx + 0.7 * scale},${cy} ${cx - 0.7 * scale},${cy}`}
        fill="#94a3b8"
      />
      <rect
        x={cx - 5 * scale}
        y={cy - 2.5 * scale}
        width={10 * scale}
        height={5 * scale}
        rx={1.5 * scale}
        fill="#64748b"
      />
      <g transform={`translate(${cx + 3 * scale} ${cy})`}>
        <g className={spinClass} transform={`scale(${scale})`}>
          {[0, 120, 240].map((angle) => (
            <path
              key={angle}
              d="M 0 0 C -1 -7 -1.6 -16 -0.6 -24 C 0 -25 0.6 -25 0.9 -24 C 1.6 -16 1 -7 0 0 Z"
              fill="#cbd5e1"
              transform={`rotate(${angle})`}
            />
          ))}
          {/* phantom bounding circle — makes fill-box symmetric so pivot = hub */}
          <circle cx="0" cy="0" r="26" fill="none" />
          <circle cx="0" cy="0" r="1.4" fill="#64748b" />
        </g>
      </g>
      <ellipse cx={cx} cy={towerBottom + 2} rx={6 * scale} ry={1.2 * scale} fill="#0f172a" opacity="0.05" />
      {/* tower height anchor (avoids unused var lint) */}
      <line x1={cx} y1={cy} x2={cx} y2={cy + towerHeight} stroke="transparent" />
    </g>
  );
}

// ---------- HYBRID ----------
function HybridIllustration({ className, style }: VariantProps) {
  return (
    <svg {...SVG_PROPS} className={className} style={style}>
      <title>Hybrid solar and wind project illustration</title>
      <defs>
        <radialGradient id="epc-hybrid-sun" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff7d6" />
          <stop offset="50%" stopColor="var(--epc-accent)" />
          <stop offset="100%" stopColor="var(--epc-accent)" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="epc-hybrid-blade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--epc-accent)" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#cbd5e1" stopOpacity="0.95" />
        </linearGradient>
      </defs>

      {/* Soft vertical seam */}
      <line x1="120" y1="14" x2="120" y2="110" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="2 3" opacity="0.6" />

      {/* Horizon */}
      <line x1="0" y1="96" x2="240" y2="96" stroke="#e2e8f0" strokeWidth="1" />

      {/* --- LEFT HALF: sun + small panel --- */}
      <circle cx="50" cy="38" r="22" fill="url(#epc-hybrid-sun)" opacity="0.4" />
      <g transform="translate(50 38)">
        <g className="epc-illust-spin-sun">
          {Array.from({ length: 8 }).map((_, i) => {
            const angle = (i * 360) / 8;
            return (
              <line
                key={i}
                x1="0"
                y1="-18"
                x2="0"
                y2="-24"
                stroke="var(--epc-accent)"
                strokeWidth="1.4"
                strokeLinecap="round"
                opacity="0.75"
                transform={`rotate(${angle})`}
              />
            );
          })}
        </g>
      </g>
      <circle cx="50" cy="38" r="7" fill="var(--epc-accent)" className="epc-illust-pulse" />
      <circle cx="50" cy="38" r="3.5" fill="#fff8d6" opacity="0.9" />

      {/* Left panel */}
      <g>
        <polygon points="22,96 92,96 100,76 14,76" fill="#cbd5e1" stroke="#94a3b8" strokeWidth="0.7" />
        {Array.from({ length: 4 }).map((_, i) => {
          const t = (i + 1) / 5;
          const xTop = 14 + (100 - 14) * t;
          const xBot = 22 + (92 - 22) * t;
          return <line key={`hyL-${i}`} x1={xTop} y1="76" x2={xBot} y2="96" stroke="#94a3b8" strokeWidth="0.55" />;
        })}
        <line x1="18" y1="86" x2="96" y2="86" stroke="#94a3b8" strokeWidth="0.55" />
        <line x1="40" y1="96" x2="38" y2="104" stroke="#64748b" strokeWidth="1.2" strokeLinecap="round" />
        <line x1="74" y1="96" x2="76" y2="104" stroke="#64748b" strokeWidth="1.2" strokeLinecap="round" />
        <ellipse cx="56" cy="100" rx="36" ry="2" fill="#0f172a" opacity="0.05" />
      </g>

      {/* --- RIGHT HALF: turbine --- */}
      <g>
        <polygon points="174,96 168,96 170,42 172,42" fill="#94a3b8" />
        <rect x="164" y="36" width="14" height="7" rx="2" fill="#64748b" />
        <g transform="translate(176 40)">
          <g className="epc-illust-spin">
            {[0, 120, 240].map((angle) => (
              <path
                key={angle}
                d="M 0 0 C -1.4 -9 -2.2 -20 -0.9 -30 C 0 -32 0.9 -32 1.2 -30 C 2.2 -20 1.4 -9 0 0 Z"
                fill="url(#epc-hybrid-blade)"
                stroke="var(--epc-accent)"
                strokeOpacity="0.3"
                strokeWidth="0.5"
                transform={`rotate(${angle})`}
              />
            ))}
            {/* phantom bounding circle — makes fill-box symmetric so pivot = hub */}
            <circle cx="0" cy="0" r="33" fill="none" />
            <circle cx="0" cy="0" r="1.8" fill="#475569" />
          </g>
        </g>
        <ellipse cx="171" cy="100" rx="11" ry="1.8" fill="#0f172a" opacity="0.07" />
      </g>
    </svg>
  );
}

// ---------- BESS ----------
function BessIllustration({ className, style }: VariantProps) {
  // Four battery modules across the bottom area, each with a charge fill.
  const moduleCount = 4;
  const moduleWidth = 38;
  const moduleHeight = 56;
  const gap = 8;
  const totalWidth = moduleCount * moduleWidth + (moduleCount - 1) * gap;
  const startX = (240 - totalWidth) / 2;
  const moduleY = 38;

  return (
    <svg {...SVG_PROPS} className={className} style={style}>
      <title>Battery energy storage project illustration</title>
      <defs>
        <linearGradient id="epc-bess-charge" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="var(--epc-accent)" stopOpacity="0.95" />
          <stop offset="100%" stopColor="var(--epc-accent)" stopOpacity="0.5" />
        </linearGradient>
      </defs>

      {/* Horizon shadow */}
      <ellipse cx="120" cy="102" rx="100" ry="3" fill="#0f172a" opacity="0.05" />

      {/* Battery modules */}
      {Array.from({ length: moduleCount }).map((_, i) => {
        const x = startX + i * (moduleWidth + gap);
        const delay = `${i * 0.4}s`;
        return (
          <g key={i}>
            {/* Shell */}
            <rect
              x={x}
              y={moduleY}
              width={moduleWidth}
              height={moduleHeight}
              rx="3.5"
              fill="#e2e8f0"
              stroke="#94a3b8"
              strokeWidth="0.9"
            />
            {/* Terminal nubs */}
            <rect x={x + 6} y={moduleY - 2.5} width="6" height="3" rx="0.8" fill="#94a3b8" />
            <rect x={x + moduleWidth - 12} y={moduleY - 2.5} width="6" height="3" rx="0.8" fill="#94a3b8" />
            {/* Internal cell divisions (3 cells per module) */}
            <line
              x1={x + moduleWidth / 3}
              y1={moduleY + 2}
              x2={x + moduleWidth / 3}
              y2={moduleY + moduleHeight - 2}
              stroke="#cbd5e1"
              strokeWidth="0.6"
            />
            <line
              x1={x + (2 * moduleWidth) / 3}
              y1={moduleY + 2}
              x2={x + (2 * moduleWidth) / 3}
              y2={moduleY + moduleHeight - 2}
              stroke="#cbd5e1"
              strokeWidth="0.6"
            />
            {/* Charge fill — anchored to bottom of module via y/height. We use a
                separate clipPath so animating height looks like rising fluid. */}
            <clipPath id={`epc-bess-clip-${i}`}>
              <rect x={x + 2.5} y={moduleY + 2.5} width={moduleWidth - 5} height={moduleHeight - 5} rx="2" />
            </clipPath>
            <g clipPath={`url(#epc-bess-clip-${i})`}>
              {/* Use a flipped inner SVG so the rect's top edge becomes the
                  visible bottom — animating `height` then appears to fill
                  upward like a liquid charge. */}
              <svg
                x={x + 2.5}
                y={moduleY + 2.5}
                width={moduleWidth - 5}
                height={moduleHeight - 5}
                viewBox={`0 0 ${moduleWidth - 5} ${moduleHeight - 5}`}
                preserveAspectRatio="none"
                style={{ transform: 'scaleY(-1)', transformOrigin: 'center' }}
                aria-hidden="true"
              >
                <title>battery charge fill</title>
                <rect
                  className="epc-illust-charge-flow"
                  x="0"
                  y="0"
                  width={moduleWidth - 5}
                  height="20%"
                  fill="url(#epc-bess-charge)"
                  style={{ animationDelay: delay }}
                />
              </svg>
            </g>
          </g>
        );
      })}

      {/* Lightning bolt above the middle */}
      <g transform="translate(120 22)" className="epc-illust-pulse-fast">
        <path
          d="M -3 -10 L 4 -2 L 0 -2 L 3 8 L -4 0 L 0 0 Z"
          fill="var(--epc-accent)"
          stroke="var(--epc-accent)"
          strokeWidth="0.4"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

// ---------- GENERIC ----------
function GenericIllustration({ className, style }: VariantProps) {
  return (
    <svg {...SVG_PROPS} className={className} style={style}>
      <title>Project illustration</title>
      <defs>
        <pattern id="epc-generic-grid" x="0" y="0" width="16" height="16" patternUnits="userSpaceOnUse">
          <path d="M 16 0 L 0 0 0 16" fill="none" stroke="#e2e8f0" strokeWidth="0.5" />
        </pattern>
        <linearGradient id="epc-generic-scan" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--epc-accent)" stopOpacity="0" />
          <stop offset="50%" stopColor="var(--epc-accent)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="var(--epc-accent)" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Grid background */}
      <rect x="0" y="0" width="240" height="120" fill="url(#epc-generic-grid)" />

      {/* Abstract architectural shapes (slate strokes, no fill) */}
      <rect x="36" y="32" width="76" height="60" rx="3" fill="none" stroke="#94a3b8" strokeWidth="1" />
      <rect
        x="120"
        y="46"
        width="80"
        height="46"
        rx="3"
        fill="none"
        stroke="#94a3b8"
        strokeWidth="1"
        transform="rotate(-3 160 69)"
      />
      {/* Internal partition lines */}
      <line x1="36" y1="58" x2="112" y2="58" stroke="#cbd5e1" strokeWidth="0.7" />
      <line x1="74" y1="32" x2="74" y2="92" stroke="#cbd5e1" strokeWidth="0.7" />
      {/* Small node marks */}
      <circle cx="36" cy="32" r="1.8" fill="var(--epc-accent)" />
      <circle cx="112" cy="92" r="1.8" fill="var(--epc-accent)" />
      <circle cx="200" cy="46" r="1.8" fill="var(--epc-accent)" opacity="0.7" />

      {/* Scan band — drawn at x=0, animated via translateX */}
      <g className="epc-illust-scan">
        <rect x="0" y="0" width="30" height="120" fill="url(#epc-generic-scan)" />
      </g>
    </svg>
  );
}

// ============================================================================
// MINI ICON — 32x32 animated category badge for compact card meta rows.
// Same animations as the hero scenes but tuned for tiny scale. Use this when
// the card layout doesn't have room for a full illustration but still wants
// instant project-type recognition.
// ============================================================================

export interface EpcProjectMiniIconProps {
  variant: EpcIllustrationVariant;
  accent: string;
  className?: string;
}

export function EpcProjectMiniIcon({ variant, accent, className }: EpcProjectMiniIconProps) {
  const style: SvgRootStyle = { '--epc-accent': accent };
  const rootClass = `epc-illust-root${className ? ` ${className}` : ''}`;
  const gid = useId().replace(/:/g, '');

  switch (variant) {
    case 'solar': {
      // Proper PV module: champagne aluminium frame (theme accent), a
      // gradient-filled mono-cell laminate with cell grid + busbars, and an
      // animated sheen sweeping across the glass. IDs are instance-unique so a
      // grid of cards doesn't cross-reference the wrong gradient/clip.
      const cellsId = `${gid}-cells`;
      const gleamId = `${gid}-gleam`;
      const clipId = `${gid}-clip`;
      return (
        <svg {...MINI_SVG_PROPS} className={rootClass} style={style}>
          <title>Solar</title>
          <defs>
            <linearGradient id={cellsId} x1="0" y1="0" x2="0.35" y2="1">
              <stop offset="0" stopColor="#1A4E78" />
              <stop offset="0.5" stopColor="#103252" />
              <stop offset="1" stopColor="#0A2236" />
            </linearGradient>
            <linearGradient id={gleamId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#ffffff" stopOpacity="0" />
              <stop offset="0.5" stopColor="#ffffff" stopOpacity="0.9" />
              <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>
            <clipPath id={clipId}>
              <rect x="6.1" y="8.1" width="19.8" height="15.8" rx="1" />
            </clipPath>
          </defs>

          {/* mounting legs (accent) + contact shadow */}
          <g stroke="var(--epc-accent)" strokeWidth="1.3" strokeLinecap="round">
            <line x1="11" y1="24.6" x2="10" y2="29" />
            <line x1="21" y1="24.6" x2="22" y2="29" />
          </g>
          <ellipse cx="16" cy="29.7" rx="9" ry="1.1" fill="#0f172a" opacity="0.12" />

          {/* aluminium frame (theme accent) with engraved hairline */}
          <rect
            x="4.6" y="6.6" width="22.8" height="18.4" rx="2"
            fill="var(--epc-accent)" stroke="#2A1F17" strokeOpacity="0.35" strokeWidth="0.5"
          />
          <rect x="4.6" y="6.6" width="22.8" height="18.4" rx="2" fill="none" stroke="#ffffff" strokeOpacity="0.22" strokeWidth="0.5" />

          {/* laminate — gradient glass */}
          <rect x="6.1" y="8.1" width="19.8" height="15.8" rx="1" fill={`url(#${cellsId})`} />

          {/* cell grid: 4 columns x 3 rows */}
          <g stroke="#cfe3f5" strokeOpacity="0.18" strokeWidth="0.7">
            <line x1="11.05" y1="8.1" x2="11.05" y2="23.9" />
            <line x1="16.0" y1="8.1" x2="16.0" y2="23.9" />
            <line x1="20.95" y1="8.1" x2="20.95" y2="23.9" />
            <line x1="6.1" y1="13.37" x2="25.9" y2="13.37" />
            <line x1="6.1" y1="18.63" x2="25.9" y2="18.63" />
          </g>
          {/* twin busbars down each cell column */}
          <g stroke="#cfe3f5" strokeOpacity="0.11" strokeWidth="0.4">
            <line x1="8.6" y1="8.1" x2="8.6" y2="23.9" />
            <line x1="13.5" y1="8.1" x2="13.5" y2="23.9" />
            <line x1="18.4" y1="8.1" x2="18.4" y2="23.9" />
            <line x1="23.3" y1="8.1" x2="23.3" y2="23.9" />
          </g>

          {/* animated gradient sheen sweeping across the glass */}
          <g clipPath={`url(#${clipId})`}>
            <rect className="epc-illust-gleam-panel" x="6.1" y="6.5" width="4.5" height="19" fill={`url(#${gleamId})`} />
          </g>
        </svg>
      );
    }

    case 'wind': {
      const wBladeId = `${gid}-wblade`;
      return (
        <svg {...MINI_SVG_PROPS} className={rootClass} style={style}>
          <title>Wind</title>
          <defs>
            <linearGradient id={wBladeId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="var(--epc-accent)" stopOpacity="0.65" />
              <stop offset="100%" stopColor="#cbd5e1" stopOpacity="0.95" />
            </linearGradient>
          </defs>
          {/* Tower — tapered mast, wider at base */}
          <polygon points="15.1,29.5 16.9,29.5 16.5,13.5 15.5,13.5" fill="#94a3b8" />
          <line x1="16" y1="13.5" x2="16" y2="29.5" stroke="#cbd5e1" strokeWidth="0.5" opacity="0.6" />
          {/* Nacelle */}
          <rect x="12.2" y="12" width="7.8" height="3" rx="1.2" fill="#64748b" />
          {/* Hub + 3-blade rotor — geometry at (0,0), outer translate positions to hub centre.
              Hub at y=13.5 keeps the upward blade (tip at y≈3.5) inside the 32-unit viewbox. */}
          <g transform="translate(16 13.5)">
            <g className="epc-illust-spin">
              {[0, 120, 240].map((angle) => (
                <path
                  key={angle}
                  d="M 0 0 C -1.6 -3 -2.4 -6.5 -1.3 -9.5 C -0.4 -10.5 0.4 -10.5 1.3 -9.5 C 2.4 -6.5 1.6 -3 0 0 Z"
                  fill={`url(#${wBladeId})`}
                  stroke="var(--epc-accent)"
                  strokeOpacity="0.3"
                  strokeWidth="0.4"
                  transform={`rotate(${angle})`}
                />
              ))}
              {/* phantom bounding circle — makes fill-box symmetric so pivot = hub */}
              <circle cx="0" cy="0" r="11" fill="none" />
              {/* hub cap */}
              <circle cx="0" cy="0" r="1.5" fill="#475569" />
              <circle cx="0" cy="0" r="0.65" fill="#94a3b8" />
            </g>
          </g>
          {/* ground shadow */}
          <ellipse cx="16" cy="30" rx="5.5" ry="0.8" fill="#0f172a" opacity="0.12" />
        </svg>
      );
    }

    case 'hybrid':
      return (
        <svg {...MINI_SVG_PROPS} className={rootClass} style={style}>
          <title>Hybrid</title>
          {/* left: small sun */}
          <g transform="translate(9 13)">
            <g className="epc-illust-spin-sun">
              {Array.from({ length: 6 }).map((_, i) => (
                <line
                  key={i}
                  x1="0"
                  y1="-5"
                  x2="0"
                  y2="-7"
                  stroke="var(--epc-accent)"
                  strokeWidth="1"
                  strokeLinecap="round"
                  transform={`rotate(${i * 60})`}
                />
              ))}
            </g>
          </g>
          <circle cx="9" cy="13" r="3" fill="var(--epc-accent)" className="epc-illust-pulse" />
          {/* right: small turbine */}
          <polygon points="22.6,28 24.4,28 23.8,12 23.2,12" fill="#94a3b8" />
          <rect x="21" y="11" width="5" height="2.4" rx="0.8" fill="#64748b" />
          <g transform="translate(23.5 12.2)">
            <g className="epc-illust-spin">
              {[0, 120, 240].map((angle) => (
                <path
                  key={angle}
                  d="M 0 0 C -0.7 -2.5 -0.9 -6 -0.3 -8 C 0 -8.3 0.3 -8.3 0.5 -8 C 0.9 -6 0.7 -2.5 0 0 Z"
                  fill="var(--epc-accent)"
                  fillOpacity="0.8"
                  transform={`rotate(${angle})`}
                />
              ))}
              {/* phantom bounding circle — makes fill-box symmetric so pivot = hub */}
              <circle cx="0" cy="0" r="9" fill="none" />
              <circle cx="0" cy="0" r="0.7" fill="#475569" />
            </g>
          </g>
        </svg>
      );

    case 'bess':
      return (
        <svg {...MINI_SVG_PROPS} className={rootClass} style={style}>
          <title>Battery storage</title>
          <defs>
            <linearGradient id="epc-bess-mini-charge" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="var(--epc-accent)" stopOpacity="0.95" />
              <stop offset="100%" stopColor="var(--epc-accent)" stopOpacity="0.5" />
            </linearGradient>
          </defs>
          {/* 3 mini batteries */}
          {[0, 1, 2].map((i) => {
            const x = 5 + i * 7.5;
            const clipId = `epc-bess-mini-clip-${i}`;
            return (
              <g key={i}>
                <rect x={x} y="9" width="6" height="18" rx="1.2" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="0.5" />
                <rect x={x + 1} y="7.5" width="4" height="1.5" rx="0.3" fill="#94a3b8" />
                <clipPath id={clipId}>
                  <rect x={x + 1} y="10" width="4" height="16" rx="0.8" />
                </clipPath>
                <g clipPath={`url(#${clipId})`}>
                  <svg
                    x={x + 1}
                    y="10"
                    width="4"
                    height="16"
                    viewBox="0 0 4 16"
                    preserveAspectRatio="none"
                    style={{ transform: 'scaleY(-1)', transformOrigin: 'center' }}
                    aria-hidden="true"
                  >
                    <title>charge</title>
                    <rect
                      className="epc-illust-charge-flow"
                      x="0"
                      y="0"
                      width="4"
                      height="20%"
                      fill="url(#epc-bess-mini-charge)"
                      style={{ animationDelay: `${i * 0.35}s` }}
                    />
                  </svg>
                </g>
              </g>
            );
          })}
        </svg>
      );

    default:
      return (
        <svg {...MINI_SVG_PROPS} className={rootClass} style={style}>
          <title>Project</title>
          <rect x="6" y="6" width="20" height="20" rx="3" fill="none" stroke="var(--epc-accent)" strokeWidth="1.5" />
          <circle cx="16" cy="16" r="2.6" fill="var(--epc-accent)" className="epc-illust-pulse" />
        </svg>
      );
  }
}

// ============================================================================
// WATERMARK — a thin wrapper that renders the full hero illustration at low
// opacity, intended to be absolutely positioned inside a card so it sits
// behind the body content as a soft category cue (no border, no box).
// ============================================================================

export interface EpcProjectWatermarkProps {
  variant: EpcIllustrationVariant;
  accent: string;
  /** 0-1, default 0.10. */
  opacity?: number;
  className?: string;
}

export function EpcProjectWatermark({ variant, accent, opacity = 0.1, className }: EpcProjectWatermarkProps) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none select-none${className ? ` ${className}` : ''}`}
      style={{ opacity }}
    >
      <EpcProjectIllustration variant={variant} accent={accent} />
    </div>
  );
}
